import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GraficoCalendario, GraficoUso } from '../../components/gestion';
import gestionService, {
  DatosPorDia,
  DatosUso,
  ResumenMaquina,
} from '../../services/gestionService';
import toast from 'react-hot-toast';
import { toISODateSafe } from '../../utils/dateUtils';

type TabActiva = 'calendario' | 'uso';

export const OperacionView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tabActiva, setTabActiva] = useState<TabActiva>('calendario');

  // Estado para datos
  const [datosCalendario, setDatosCalendario] = useState<DatosPorDia[]>([]);
  const [, setDatosUso] = useState<DatosUso[]>([]);
  // resumen loaded but rendered in future section
  const [, setResumen] = useState<ResumenMaquina | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar datos al montar - con rango amplio para poder navegar por meses
  useEffect(() => {
    const cargarDatos = async () => {
      setCargando(true);
      setError(null);

      try {
        // Calcular rango amplio (últimos 90 días)
        const hoy = new Date();
        const hace90Dias = new Date();
        hace90Dias.setDate(hace90Dias.getDate() - 90);

        const fechaInicio = toISODateSafe(hace90Dias);
        const fechaFin = toISODateSafe(hoy);

        // Cargar datos del calendario
        const datosCalendarioRes = await gestionService.obtenerDatosCalendario(
          fechaInicio,
          fechaFin
        );
        setDatosCalendario(datosCalendarioRes);

        // Cargar datos de uso
        const datosUsoRes = await gestionService.obtenerDatosUso();
        setDatosUso(datosUsoRes);

        // Cargar resumen
        const resumenRes = await gestionService.obtenerResumenOperacion();
        setResumen(resumenRes);
      } catch (err: any) {
        const errorMsg = err instanceof Error ? err.message : 'Error al cargar datos';
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setCargando(false);
      }
    };

    cargarDatos();
  }, []);

  const handleVolver = () => {
    navigate('/gestion');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <button
          onClick={handleVolver}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-4 font-medium"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('management.back_to_management')}
        </button>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('management.operation')}</h1>
        <p className="text-gray-600">
          {t('management.usage')}
        </p>
      </div>

      {/* Contenido Principal */}
      <div className="max-w-7xl mx-auto">
        {/* Tabs */}
        <div className="flex space-x-2 mb-8 border-b border-gray-200">
          <button
            onClick={() => setTabActiva('calendario')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              tabActiva === 'calendario'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              {t('management.calendar')}
            </div>
          </button>

          <button
            onClick={() => setTabActiva('uso')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              tabActiva === 'uso'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t('management.usage')}
            </div>
          </button>
        </div>

        {/* Contenido de Tabs */}
        {tabActiva === 'calendario' && (
          <div className="space-y-6">
            {/* Gráfico */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('management.calendar')}
              </h2>
              <p className="text-gray-600 mb-6">
                {t('management.operation')}
              </p>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  Error: {error}
                </div>
              )}

              <GraficoCalendario
                datos={datosCalendario}
                isLoading={cargando}
                horasDisponibles={24}
              />
            </div>
          </div>
        )}

        {tabActiva === 'uso' && (
          <div className="space-y-6">
            {/* Gráfico */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('management.usage')}
              </h2>
              <p className="text-gray-600 mb-6">
                {t('common.hours')}
              </p>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  Error: {error}
                </div>
              )}

              <GraficoUso datos={datosCalendario} isLoading={cargando} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OperacionView;
